import React, { useState, useEffect } from 'react';
import { Info, Save, Loader2, RefreshCw, BarChart3, Mountain } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAbout = () => {
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const { token } = useAuth();

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/about`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContent(response.data);
    } catch (error) {
      console.error('Error fetching about content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await axios.put(`${API_URL}/api/admin/about`, content, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving about content:', error);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setContent({ ...content, [field]: value });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-teal-500" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-about-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">About Page Editor</h1>
            <p className="text-gray-600">Edit the content displayed on the About page</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchContent} data-testid="reset-btn">
              <RefreshCw className="h-4 w-4 mr-2" />
              Reset
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-gradient-to-r from-blue-500 to-teal-500 text-white"
              data-testid="save-btn"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {saved ? 'Saved!' : 'Save Changes'}
            </Button>
          </div>
        </div>

        {/* Hero Section */}
        <Card className="border-2 border-blue-200">
          <CardHeader className="bg-blue-50">
            <CardTitle className="flex items-center gap-2">
              <Info className="h-5 w-5 text-blue-600" />
              Hero Section
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            <div>
              <label className="block text-sm font-medium mb-1">Title</label>
              <Input
                value={content.hero_title || ''}
                onChange={(e) => updateField('hero_title', e.target.value)}
                placeholder="Our Mission"
                data-testid="hero-title-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Subtitle</label>
              <Input
                value={content.hero_subtitle || ''}
                onChange={(e) => updateField('hero_subtitle', e.target.value)}
                placeholder="Helping Dads Build Stronger Bonds"
                data-testid="hero-subtitle-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Mission Section */}
        <Card className="border-2 border-green-200">
          <CardHeader className="bg-green-50">
            <CardTitle>Mission Section - "Why Dad Connect Exists"</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            <div>
              <label className="block text-sm font-medium mb-1">Section Title</label>
              <Input
                value={content.mission_title || ''}
                onChange={(e) => updateField('mission_title', e.target.value)}
                placeholder="Why Dad Connect Exists"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Content</label>
              <textarea
                value={content.mission_content || ''}
                onChange={(e) => updateField('mission_content', e.target.value)}
                className="w-full border rounded-md p-2 min-h-[200px]"
                placeholder="Write your mission statement... Use double line breaks for paragraphs."
              />
              <p className="text-xs text-gray-500 mt-1">Use double line breaks to create paragraphs</p>
            </div>
          </CardContent>
        </Card>

        {/* Stats Section */}
        <Card className="border-2 border-blue-200">
          <CardHeader className="bg-blue-50">
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-600" />
              Statistics Section - "The Power of an Involved Dad"
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            <div>
              <label className="block text-sm font-medium mb-1">Section Title</label>
              <Input
                value={content.stats_title || ''}
                onChange={(e) => updateField('stats_title', e.target.value)}
                placeholder="The Power of an Involved Dad"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Content (Statistics & Research)</label>
              <textarea
                value={content.stats_content || ''}
                onChange={(e) => updateField('stats_content', e.target.value)}
                className="w-full border rounded-md p-2 min-h-[250px]"
                placeholder="Include statistics about the success of kids with active dads..."
              />
              <p className="text-xs text-gray-500 mt-1">Use **text** for bold. Double line breaks for paragraphs.</p>
            </div>
          </CardContent>
        </Card>

        {/* Challenges Section */}
        <Card className="border-2 border-orange-200">
          <CardHeader className="bg-orange-50">
            <CardTitle className="flex items-center gap-2">
              <Mountain className="h-5 w-5 text-orange-600" />
              Challenges Section - "We Know It's Not Easy"
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            <div>
              <label className="block text-sm font-medium mb-1">Section Title</label>
              <Input
                value={content.challenges_title || ''}
                onChange={(e) => updateField('challenges_title', e.target.value)}
                placeholder="We Know It's Not Easy"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Content (Difficulties Dads Face)</label>
              <textarea
                value={content.challenges_content || ''}
                onChange={(e) => updateField('challenges_content', e.target.value)}
                className="w-full border rounded-md p-2 min-h-[250px]"
                placeholder="Discuss the challenges dads face and how it takes work and sacrifice..."
              />
              <p className="text-xs text-gray-500 mt-1">Use **text** for bold. Use - or • for bullet points.</p>
            </div>
          </CardContent>
        </Card>

        {/* How To Section */}
        <Card className="border-2 border-purple-200">
          <CardHeader className="bg-purple-50">
            <CardTitle>How To Get The Most Out of Dad Connect</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            <div>
              <label className="block text-sm font-medium mb-1">Section Title</label>
              <Input
                value={content.howto_title || ''}
                onChange={(e) => updateField('howto_title', e.target.value)}
                placeholder="Getting the Most Out of Dad Connect"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Intro Text</label>
              <textarea
                value={content.howto_intro || ''}
                onChange={(e) => updateField('howto_intro', e.target.value)}
                className="w-full border rounded-md p-2"
                rows={2}
              />
            </div>
            
            {[1, 2, 3, 4].map((num) => (
              <div key={num} className="p-4 bg-gray-50 rounded-lg space-y-2">
                <p className="font-medium text-sm text-gray-500">Item {num}</p>
                <Input
                  value={content[`howto_${num}_title`] || ''}
                  onChange={(e) => updateField(`howto_${num}_title`, e.target.value)}
                  placeholder="Title"
                />
                <textarea
                  value={content[`howto_${num}_content`] || ''}
                  onChange={(e) => updateField(`howto_${num}_content`, e.target.value)}
                  className="w-full border rounded-md p-2"
                  rows={2}
                  placeholder="Description..."
                />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Coaching Section */}
        <Card className="border-2 border-teal-200">
          <CardHeader className="bg-teal-50">
            <CardTitle>Coaching Section - Why Coaching Accelerates Growth</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            <div>
              <label className="block text-sm font-medium mb-1">Section Title</label>
              <Input
                value={content.coaching_title || ''}
                onChange={(e) => updateField('coaching_title', e.target.value)}
                placeholder="Why Coaching Accelerates Your Growth"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Content</label>
              <textarea
                value={content.coaching_content || ''}
                onChange={(e) => updateField('coaching_content', e.target.value)}
                className="w-full border rounded-md p-2 min-h-[200px]"
                placeholder="Write about coaching benefits... Use **text** for bold and double line breaks for paragraphs."
              />
              <p className="text-xs text-gray-500 mt-1">Use **text** for bold and double line breaks for paragraphs</p>
            </div>
          </CardContent>
        </Card>

        {/* Bottom Line Section */}
        <Card className="border-2 border-gray-200">
          <CardHeader className="bg-gray-50">
            <CardTitle>Bottom Line Section</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            <div>
              <label className="block text-sm font-medium mb-1">Section Title</label>
              <Input
                value={content.bottomline_title || ''}
                onChange={(e) => updateField('bottomline_title', e.target.value)}
                placeholder="The Bottom Line"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Content</label>
              <textarea
                value={content.bottomline_content || ''}
                onChange={(e) => updateField('bottomline_content', e.target.value)}
                className="w-full border rounded-md p-2 min-h-[150px]"
                placeholder="Final message to visitors..."
              />
            </div>
          </CardContent>
        </Card>

        {/* CTA Section */}
        <Card className="border-2 border-green-200">
          <CardHeader className="bg-green-50">
            <CardTitle>Call to Action</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            <div>
              <label className="block text-sm font-medium mb-1">CTA Heading</label>
              <Input
                value={content.cta_text || ''}
                onChange={(e) => updateField('cta_text', e.target.value)}
                placeholder="Start Your Journey Today"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Primary Button Text</label>
                <Input
                  value={content.cta_button_text || ''}
                  onChange={(e) => updateField('cta_button_text', e.target.value)}
                  placeholder="Explore Activities"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Primary Button Link</label>
                <Input
                  value={content.cta_button_link || ''}
                  onChange={(e) => updateField('cta_button_link', e.target.value)}
                  placeholder="/activities"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Secondary Button Text</label>
                <Input
                  value={content.cta_coaching_text || ''}
                  onChange={(e) => updateField('cta_coaching_text', e.target.value)}
                  placeholder="Or learn about coaching"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Secondary Button Link</label>
                <Input
                  value={content.cta_coaching_link || ''}
                  onChange={(e) => updateField('cta_coaching_link', e.target.value)}
                  placeholder="/coaching"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Button (bottom) */}
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            size="lg"
            className="bg-gradient-to-r from-blue-500 to-teal-500 text-white"
            data-testid="save-btn-bottom"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {saved ? 'Saved!' : 'Save All Changes'}
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminAbout;
